<template>
  <div class="row">
    <div class="col-12">
      <card title="Partners">
        <table class="table table-responsive agencies-table" v-if="agencies && agencies.length">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Country</th>
              <th>Is active</th>
              <th>Manager</th>
              <th>Login</th>
              <th>Created</th>
              <th>Activated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="agency in agencies"
              :key="agency.id"
              class="agencies-table__row"
              :class="{ 'agencies-table__row--inactive': agency.is_hide }"
              @click="openEdit(agency.id)"
              @mousedown.middle="openEdit(agency.id)"
            >
              <td>{{ agency.id }}</td>
              <td>{{ agency.name && agency.name.en }}</td>
              <td>{{ agency.iso_code }}</td>
              <td>{{ agency.is_hide ? 'No' : 'Yes' }}</td>
              <td>{{ getManagerName(agency.partnership_manager_id) }}</td>
              <td>{{ agency.login }}</td>
              <td>{{ formatDate(agency.created_at) }}</td>
              <td>{{ formatDate(agency.activated_at) }}</td>
              <td class="agencies-table__actions" @click.stop>
                <button
                  type="button"
                  class="btn btn-sm agencies-table__pdf-btn"
                  :class="{ 'agencies-table__pdf-btn--filled': agency.has_presentations }"
                  :title="agency.has_presentations ? 'General presentation' : 'General presentation (none uploaded yet)'"
                  @click="openPresentations(agency.id)"
                >
                  <i class="fa fa-file-pdf"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-sm agencies-table__commission-btn"
                  title="Commission"
                  @click="openCommission(agency)"
                >
                  <i class="fa fa-percent"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  v-if="canManageAgencies"
                  :disabled="impersonating === agency.id"
                  :title="`Login as agency owner (${agency.name && agency.name.en})`"
                  @click="impersonate(agency.id)"
                >
                  <i v-if="impersonating === agency.id" class="tim-icons icon-refresh-02 agencies-table__spinner"></i>
                  <i v-else class="fa fa-sign-in-alt"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="card-body" v-else>No data</div>
      </card>

      <agency-presentations-modal
        v-if="presentationsAgencyId"
        :visible.sync="presentationsModalVisible"
        :agency-id="presentationsAgencyId"
      />
    </div>
  </div>
</template>

<script>
import agencyAccess from '@/components/Agencies/agencyAccess';
import { mapState } from 'vuex';
import APIAgencies from '@/api/modules/agencies';
import AgencyPresentationsModal from './AgencyPresentationsModal.vue';
import { openPartnerCabinet } from './agencyImpersonation';

export default {
  mixins: [agencyAccess],
  components: { AgencyPresentationsModal },
  data: () => ({
    impersonating: null,
    presentationsAgencyId: null,
    presentationsModalVisible: false,
  }),
  computed: {
    ...mapState('agencies', ['agencies', 'managers']),
    adminDomain() {
      if (process.env.NODE_ENV === 'development') {
        return 'https://admin.bookimed.run';
      }
      const tld = window.location.hostname.slice(window.location.hostname.lastIndexOf('.'));
      return `https://admin.bookimed${tld}`;
    },
  },
  methods: {
    openPresentations(agencyId) {
      this.presentationsAgencyId = agencyId;
      this.presentationsModalVisible = true;
    },
    openCommission(agency) {
      this.$router.push({
        name: 'AgencyCommission',
        params: { agencyId: agency.id },
        query: agency.name && agency.name.en ? { name: agency.name.en } : {},
      });
    },
    editUrl(agencyId) {
      return this.$router.resolve({ name: 'AgencyEdit', params: { id: agencyId } }).href;
    },
    openEdit(agencyId) {
      window.open(this.editUrl(agencyId), '_blank');
    },
    getManagerName(managerId) {
      if (!managerId) return '—';
      const manager = this.managers.find(m => m.id === managerId);
      return manager ? manager.name : `#${managerId}`;
    },
    formatDate(value) {
      if (!value) return '—';
      const d = new Date(value);
      if (isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    async impersonate(agencyId) {
      if (!this.canManageAgencies) return;
      this.impersonating = agencyId;
      try {
        const { data } = await APIAgencies.impersonate(agencyId);
        if (!this.canManageAgencies) return;
        openPartnerCabinet(data);
      } catch (e) {
        this.$notify({ type: 'error', title: e?.response?.data?.error?.code || e.message || 'Impersonate failed' });
      } finally {
        this.impersonating = null;
      }
    },
  },
};
</script>

<style scoped>
.agencies-table__row {
  cursor: pointer;
}
.agencies-table__row:hover {
  background-color: #e8f8d8;
}
.agencies-table__row--inactive {
  background-color: #f4f5f7;
}
.agencies-table__row--inactive:hover {
  background-color: #eceef1;
}
.agencies-table__actions {
  width: 1%;
  white-space: nowrap;
  text-align: right;
}
/* Gray by default — no presentations uploaded, the button still opens the modal to add the
   first one. Turns red (agencies-table__pdf-btn--filled) once agency.has_presentations is true,
   so the color reads as "has data" instead of always looking the same regardless of content. */
.agencies-table__pdf-btn {
  margin-right: 4px;
  background: #fff;
  border: 1px solid #ced4da;
  color: #8898aa;
}
.agencies-table__pdf-btn i {
  color: #8898aa;
}
.agencies-table__pdf-btn:hover {
  background: #f4f5f7;
  border-color: #adb5bd;
}
.agencies-table__pdf-btn:hover i {
  color: #6c757d;
}
.agencies-table__pdf-btn--filled {
  border-color: #e3342f;
  color: #e3342f;
}
.agencies-table__pdf-btn--filled i {
  color: #e3342f;
}
.agencies-table__pdf-btn--filled:hover {
  background: #fdecea;
  border-color: #c62a24;
}
.agencies-table__pdf-btn--filled:hover i {
  color: #c62a24;
}
.agencies-table__commission-btn {
  margin-right: 4px;
  background: #fff;
  border: 1px solid #ced4da;
  color: #525f7f;
}
.agencies-table__commission-btn i {
  color: #525f7f;
}
.agencies-table__commission-btn:hover {
  background: #f4f5f7;
  border-color: #adb5bd;
}
.agencies-table__spinner {
  animation: agencies-spin 1s linear infinite;
}
@keyframes agencies-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
